'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User } from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTenant } from '@/contexts/TenantContext';
import { useUser } from '@/contexts/UserContext';
import {
  useChatSessions,
  useChatMessages,
  useCreateChatSession,
  useSendMessage,
  useOptimisticMessage,
} from '@/hooks/useChatApi';
import { cn } from '@/lib/utils';
import type { ChatMessage as ChatMessageType } from '@/types/chat';

const SUPPORT_AGENT_TYPE = 'support' as const;

const markdownComponents = {
  table: ({ children, ...props }: React.HTMLAttributes<HTMLTableElement>) => (
    <div className="overflow-x-auto">
      <table {...props}>{children}</table>
    </div>
  ),
};

/**
 * Floating support chat widget — bottom-right popup.
 *
 * Replaces the Zapier chatbot. Provides general assistance
 * and structured ticket creation when users report issues.
 */
export function SupportChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const { selectedTenantId } = useTenant();
  const { userId } = useUser();

  // Fetch support sessions (reuse the single most recent one)
  const { data: sessionsData } = useChatSessions(userId, selectedTenantId, 1, 0, SUPPORT_AGENT_TYPE);

  const sessionId = sessionsData?.sessions?.[0]?.id ?? null;

  // Fetch messages for the active session
  const { data: messagesData, isLoading: isLoadingMessages } = useChatMessages(userId, selectedTenantId, sessionId);

  // Mutations
  const createSession = useCreateChatSession();
  const sendMessage = useSendMessage();
  const addOptimisticMessage = useOptimisticMessage(selectedTenantId, sessionId);

  const messages = messagesData?.messages ?? [];

  // Auto-scroll on new messages
  useEffect(() => {
    if (isOpen) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, sendMessage.isPending]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 100)}px`;
    }
  }, [message]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Ensure a support session exists, then send the message
  const handleSend = useCallback(async () => {
    const trimmed = message.trim();
    if (!trimmed || !selectedTenantId || !userId) return;

    setMessage('');

    let targetSessionId = sessionId;

    // Create session on first message
    if (!targetSessionId) {
      try {
        const session = await createSession.mutateAsync({
          userId,
          tenantId: selectedTenantId,
          agentType: SUPPORT_AGENT_TYPE,
        });
        targetSessionId = session.id;
      } catch {
        return;
      }
    }

    addOptimisticMessage(trimmed);

    try {
      await sendMessage.mutateAsync({
        userId,
        tenantId: selectedTenantId,
        sessionId: targetSessionId,
        content: trimmed,
      });
    } catch (err) {
      console.error('Support chat send failed:', err);
    }
  }, [message, sessionId, selectedTenantId, userId, createSession, sendMessage, addOptimisticMessage]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // FAB button when closed
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed bottom-6 right-6 z-[9999]',
          'w-14 h-14 rounded-full shadow-lg',
          'bg-coral text-white',
          'flex items-center justify-center',
          'hover:scale-105 hover:shadow-xl active:scale-95',
          'transition-all duration-200'
        )}
        aria-label="Open support chat"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  // Popup when open
  return (
    <div
      className={cn(
        'fixed bottom-6 right-6 z-[9999]',
        'w-[400px] h-[560px] max-h-[80vh]',
        'bg-surface-base rounded-2xl shadow-2xl border border-border-subtle',
        'flex flex-col overflow-hidden',
        'animate-in fade-in slide-in-from-bottom-4 duration-200'
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-blue-1 text-white rounded-t-2xl">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5" />
          <div>
            <p className="text-sm font-semibold leading-tight">Cortex Assistant</p>
            <p className="text-xs opacity-70">Ask anything or report an issue</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsOpen(false)}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Close chat"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {/* Welcome message (always shown) */}
        {messages.length === 0 && !isLoadingMessages && (
          <div className="flex gap-2.5">
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-1 text-white flex items-center justify-center">
              <Bot className="w-3.5 h-3.5" />
            </div>
            <div className="bg-surface-raised rounded-2xl rounded-bl-md px-3.5 py-2.5 max-w-[85%] shadow-sm">
              <p className="text-sm text-text-primary leading-relaxed">
                Hey! How can I help? I can answer questions about Cortex, help you find things, or capture a bug report
                / feature request.
              </p>
            </div>
          </div>
        )}

        {messages.map(msg => (
          <SupportMessage key={msg.id} message={msg} />
        ))}

        {/* Typing indicator */}
        {sendMessage.isPending && (
          <div className="flex gap-2.5">
            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-1 text-white flex items-center justify-center">
              <Bot className="w-3.5 h-3.5" />
            </div>
            <div className="bg-surface-raised rounded-2xl rounded-bl-md px-3.5 py-2.5 shadow-sm">
              <div className="flex items-center gap-1">
                <span
                  className="w-1.5 h-1.5 bg-text-placeholder rounded-full animate-bounce"
                  style={{ animationDelay: '0ms' }}
                />
                <span
                  className="w-1.5 h-1.5 bg-text-placeholder rounded-full animate-bounce"
                  style={{ animationDelay: '150ms' }}
                />
                <span
                  className="w-1.5 h-1.5 bg-text-placeholder rounded-full animate-bounce"
                  style={{ animationDelay: '300ms' }}
                />
              </div>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-border-subtle px-3 py-3 bg-surface-base">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            className={cn(
              'flex-1 resize-none rounded-xl border border-border-subtle px-3 py-2',
              'bg-surface-base text-text-primary text-sm placeholder:text-text-placeholder',
              'focus:outline-none focus:border-coral focus:ring-1 focus:ring-coral/20',
              'transition-all duration-200'
            )}
          />
          <button
            onClick={handleSend}
            disabled={!message.trim() || sendMessage.isPending}
            className={cn(
              'flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center',
              'bg-coral text-white',
              'hover:bg-coral/90 disabled:opacity-40 disabled:cursor-not-allowed',
              'transition-all duration-200'
            )}
          >
            {sendMessage.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact message bubble for the support popup.
 */
function SupportMessage({ message }: { message: ChatMessageType }) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex gap-2.5', isUser && 'flex-row-reverse')}>
      {/* Avatar */}
      <div
        className={cn(
          'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center',
          isUser ? 'bg-coral text-white' : 'bg-blue-1 text-white'
        )}
      >
        {isUser ? (
          message.user_initials ? (
            <span className="text-[10px] font-semibold">{message.user_initials}</span>
          ) : (
            <User className="w-3.5 h-3.5" />
          )
        ) : (
          <Bot className="w-3.5 h-3.5" />
        )}
      </div>

      {/* Bubble */}
      <div
        className={cn(
          'rounded-2xl px-3.5 py-2.5 max-w-[85%] shadow-sm',
          isUser ? 'bg-coral/10 text-text-primary rounded-br-md' : 'bg-surface-raised text-text-primary rounded-bl-md'
        )}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
        ) : (
          <div className="chat-markdown text-sm">
            <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {message.content}
            </Markdown>
          </div>
        )}
      </div>
    </div>
  );
}
